import React, { useState, useEffect, useCallback, useRef } from "react";
import { DefaultPageLayout } from "../../../../ui/layouts/DefaultPageLayout";
import { Button } from "../../../../ui/components/Button";
import { Badge } from "../../../../ui/components/Badge";
import { Tabs } from "../../../../ui/components/Tabs";
import { TextArea } from "../../../../ui/components/TextArea";
import {
  FeatherArrowLeft,
  FeatherDownload,
  FeatherUpload,
  FeatherCheck,
  FeatherRefreshCw,
  FeatherEdit2,
  FeatherX,
} from "@subframe/core";
import { useNavigate } from "react-router-dom";
import { useToast } from "../../../../shared/presentation/components";
import {
  EnrichmentJob,
  EnrichmentItem,
  downloadVendors,
  downloadCustomers,
  uploadVendors,
  uploadCustomers,
  listJobs,
  getJobItems,
  confirmAll,
  confirmItem,
  updateItem,
  retryItem,
} from "../../../../services/contextEnrichmentApi";

// ─── Status helpers ──────────────────────────────────────────────────

const jobStatusBadge = (status: string) => {
  switch (status) {
    case "uploading":
      return <Badge variant="neutral">Uploading</Badge>;
    case "processing":
      return <Badge variant="warning">Processing</Badge>;
    case "review_ready":
      return <Badge variant="brand">Review Ready</Badge>;
    case "completed":
      return <Badge variant="success">Completed</Badge>;
    default:
      return <Badge variant="neutral">{status}</Badge>;
  }
};

const itemStatusBadge = (status: string) => {
  switch (status) {
    case "draft":
      return (
        <span className="inline-flex items-center rounded-full bg-neutral-100 px-2 py-0.5 text-xs font-medium text-neutral-600">
          Draft
        </span>
      );
    case "generated":
      return (
        <span className="inline-flex items-center rounded-full bg-blue-100 px-2 py-0.5 text-xs font-medium text-blue-700">
          Generated
        </span>
      );
    case "confirmed":
      return (
        <span className="inline-flex items-center rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700">
          Confirmed
        </span>
      );
    case "failed":
      return (
        <span className="inline-flex items-center rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700">
          Failed
        </span>
      );
    default:
      return <span className="text-xs">{status}</span>;
  }
};

export const ContextEnrichmentPage: React.FC = () => {
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [uploadTab, setUploadTab] = useState<"vendors" | "customers">(
    "vendors",
  );
  const [isDownloading, setIsDownloading] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const [jobs, setJobs] = useState<EnrichmentJob[]>([]);
  const [loadingJobs, setLoadingJobs] = useState(false);

  const [expandedJobId, setExpandedJobId] = useState<number | null>(null);
  const [jobItems, setJobItems] = useState<EnrichmentItem[]>([]);
  const [loadingItems, setLoadingItems] = useState(false);

  const [editingItemId, setEditingItemId] = useState<number | null>(null);
  const [editText, setEditText] = useState("");

  // ─── Load jobs ───────────────────────────────────────────────────

  const loadJobs = useCallback(async () => {
    setLoadingJobs(true);
    try {
      const data = await listJobs();
      setJobs(data);
    } catch (e: any) {
      showError(e.message || "Failed to load jobs");
    } finally {
      setLoadingJobs(false);
    }
  }, [showError]);

  useEffect(() => {
    loadJobs();
  }, [loadJobs]);

  // // Auto-refresh disabled
  // useEffect(() => {
  //   const hasProcessing = jobs.some((j) => j.status === "processing");
  //   if (!hasProcessing || editingItemId !== null) return;
  //   const interval = setInterval(() => {
  //     loadJobs();
  //     if (expandedJobId) loadItems(expandedJobId);
  //   }, 15000);
  //   return () => clearInterval(interval);
  // }, [jobs, expandedJobId, loadJobs, editingItemId]);

  // ─── Load items ──────────────────────────────────────────────────

  const loadItems = async (jobId: number) => {
    setLoadingItems(true);
    try {
      const data = await getJobItems(jobId);
      setJobItems(data.items);
    } catch (e: any) {
      showError(e.message || "Failed to load items");
    } finally {
      setLoadingItems(false);
    }
  };

  const handleExpandJob = (jobId: number) => {
    if (expandedJobId === jobId) {
      setExpandedJobId(null);
      setJobItems([]);
    } else {
      setExpandedJobId(jobId);
      loadItems(jobId);
    }
  };

  // ─── Download ────────────────────────────────────────────────────

  const handleDownload = async (type: "vendors" | "customers") => {
    setIsDownloading(true);
    try {
      if (type === "vendors") {
        await downloadVendors();
      } else {
        await downloadCustomers();
      }
    } catch (e: any) {
      showError(e.message || "Download failed");
    } finally {
      setIsDownloading(false);
    }
  };

  // ─── Upload ──────────────────────────────────────────────────────

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) setSelectedFile(file);
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      showError("Please select a file first");
      return;
    }
    setIsUploading(true);
    try {
      const result =
        uploadTab === "vendors"
          ? await uploadVendors(selectedFile)
          : await uploadCustomers(selectedFile);

      if (result.results.errors.length > 0) {
        showError(
          `Upload completed with ${result.results.errors.length} errors. ${result.results.saved_rows} rows saved.`,
        );
      } else {
        showSuccess(
          `${result.results.saved_rows} rows uploaded successfully. GPT processing will begin shortly.`,
        );
      }
      setSelectedFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      loadJobs();
    } catch (e: any) {
      showError(e.message || "Upload failed");
    } finally {
      setIsUploading(false);
    }
  };

  // ─── Actions ─────────────────────────────────────────────────────

  const handleConfirmAll = async (jobId: number) => {
    try {
      await confirmAll(jobId);
      showSuccess("All items confirmed and pushed to main context tables");
      loadJobs();
      loadItems(jobId);
    } catch (e: any) {
      showError(e.message || "Failed to confirm all");
    }
  };

  const handleConfirmItem = async (jobId: number, itemId: number) => {
    try {
      await confirmItem(jobId, itemId);
      showSuccess("Item confirmed");
      loadItems(jobId);
      loadJobs();
    } catch (e: any) {
      showError(e.message || "Failed to confirm item");
    }
  };

  const handleRetryItem = async (jobId: number, itemId: number) => {
    try {
      await retryItem(jobId, itemId);
      showSuccess("Item queued for retry");
      loadItems(jobId);
      loadJobs();
    } catch (e: any) {
      showError(e.message || "Failed to retry item");
    }
  };

  const handleStartEdit = (item: EnrichmentItem) => {
    setEditingItemId(item.id);
    setEditText(item.final_context || "");
  };

  const handleSaveEdit = async (jobId: number, itemId: number) => {
    try {
      await updateItem(jobId, itemId, editText);
      showSuccess("Context updated");
      setEditingItemId(null);
      loadItems(jobId);
    } catch (e: any) {
      showError(e.message || "Failed to update");
    }
  };

  const handleCancelEdit = () => {
    setEditingItemId(null);
    setEditText("");
  };

  // ─── Render ──────────────────────────────────────────────────────

  return (
    <DefaultPageLayout>
      <div className="flex h-full w-full flex-col items-start">
        {/* Header */}
        <div className="flex w-full flex-col items-start gap-4 border-b border-solid border-neutral-border bg-default-background px-8 py-6">
          <Button
            variant="brand-secondary"
            icon={<FeatherArrowLeft />}
            onClick={() => navigate("/dashboard")}
          >
            Back to Dashboard
          </Button>
          <div className="flex w-full flex-col items-start gap-1">
            <span className="text-heading-2 font-heading-2 text-default-font">
              Context Enrichment
            </span>
            <span className="text-body font-body text-subtext-color">
              Download your vendors/customers, fill in context descriptions,
              upload, and let AI generate GL classification context for each
              entity.
            </span>
          </div>
        </div>

        {/* Content */}
        <div className="flex w-full grow shrink-0 basis-0 flex-col items-start gap-8 overflow-y-auto bg-default-background px-8 py-8">
          {/* Section A: Download & Upload */}
          <div className="flex w-full flex-col items-start gap-6">
            <span className="text-heading-3 font-heading-3 text-default-font">
              Step 1: Download & Fill
            </span>
            <p className="text-body font-body text-subtext-color">
              Download your existing vendors or customers as an Excel file. Fill
              in the context columns (what they supply/buy, expense/revenue
              category). Then upload the filled file.
            </p>
            <div className="flex items-center gap-4">
              <Button
                variant="neutral-secondary"
                icon={<FeatherDownload />}
                onClick={() => handleDownload("vendors")}
                disabled={isDownloading}
                loading={isDownloading}
              >
                Download Vendors
              </Button>
              <Button
                variant="neutral-secondary"
                icon={<FeatherDownload />}
                onClick={() => handleDownload("customers")}
                disabled={isDownloading}
                loading={isDownloading}
              >
                Download Customers
              </Button>
            </div>
          </div>

          {/* Upload */}
          <div className="flex w-full flex-col items-start gap-6">
            <span className="text-heading-3 font-heading-3 text-default-font">
              Step 2: Upload Filled File
            </span>

            <Tabs>
              <Tabs.Item
                active={uploadTab === "vendors"}
                onClick={() => {
                  setUploadTab("vendors");
                  setSelectedFile(null);
                }}
              >
                Vendors
              </Tabs.Item>
              <Tabs.Item
                active={uploadTab === "customers"}
                onClick={() => {
                  setUploadTab("customers");
                  setSelectedFile(null);
                }}
              >
                Customers
              </Tabs.Item>
            </Tabs>

            <div className="flex w-full items-center gap-4 rounded-md border border-dashed border-neutral-300 bg-neutral-50 p-6">
              <input
                ref={fileInputRef}
                type="file"
                accept=".xlsx,.xls"
                onChange={handleFileSelect}
                className="text-sm"
              />
              <Button
                variant="brand-primary"
                icon={<FeatherUpload />}
                onClick={handleUpload}
                disabled={!selectedFile || isUploading}
                loading={isUploading}
              >
                {isUploading
                  ? "Uploading..."
                  : `Upload ${uploadTab === "vendors" ? "Vendors" : "Customers"}`}
              </Button>
            </div>
          </div>

          {/* Section B: Job History */}
          <div className="flex w-full flex-col items-start gap-4">
            <div className="flex w-full items-center justify-between">
              <span className="text-heading-3 font-heading-3 text-default-font">
                Step 3: Review & Confirm
              </span>
              <Button
                variant="neutral-secondary"
                size="small"
                icon={<FeatherRefreshCw />}
                onClick={loadJobs}
                disabled={loadingJobs}
              >
                Refresh
              </Button>
            </div>

            {loadingJobs && jobs.length === 0 && (
              <p className="text-body font-body text-subtext-color">
                Loading jobs...
              </p>
            )}

            {!loadingJobs && jobs.length === 0 && (
              <p className="text-body font-body text-subtext-color">
                No enrichment jobs yet. Upload a file to get started.
              </p>
            )}

            {jobs.length > 0 && (
              <div className="w-full overflow-hidden rounded-md border border-solid border-neutral-200">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-neutral-200 bg-neutral-50">
                      <th className="px-4 py-3 text-left font-medium text-neutral-600">
                        Type
                      </th>
                      <th className="px-4 py-3 text-left font-medium text-neutral-600">
                        File
                      </th>
                      <th className="px-4 py-3 text-center font-medium text-neutral-600">
                        Total
                      </th>
                      <th className="px-4 py-3 text-center font-medium text-neutral-600">
                        Processed
                      </th>
                      <th className="px-4 py-3 text-center font-medium text-neutral-600">
                        Failed
                      </th>
                      <th className="px-4 py-3 text-center font-medium text-neutral-600">
                        Status
                      </th>
                      <th className="px-4 py-3 text-left font-medium text-neutral-600">
                        Date
                      </th>
                      <th className="px-4 py-3 text-center font-medium text-neutral-600">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {jobs.map((job) => (
                      <React.Fragment key={job.id}>
                        <tr
                          className={`border-b border-neutral-100 cursor-pointer hover:bg-neutral-50 ${expandedJobId === job.id ? "bg-neutral-50" : ""}`}
                          onClick={() => handleExpandJob(job.id)}
                        >
                          <td className="px-4 py-3 capitalize">{job.type}</td>
                          <td className="px-4 py-3 max-w-[200px] truncate">
                            {job.file_name}
                          </td>
                          <td className="px-4 py-3 text-center">
                            {job.total_rows}
                          </td>
                          <td className="px-4 py-3 text-center">
                            {job.processed_rows}
                          </td>
                          <td className="px-4 py-3 text-center">
                            {job.failed_rows}
                          </td>
                          <td className="px-4 py-3 text-center">
                            {jobStatusBadge(job.status)}
                          </td>
                          <td className="px-4 py-3 text-xs text-neutral-500">
                            {new Date(job.created_at).toLocaleDateString()}
                          </td>
                          <td className="px-4 py-3 text-center"></td>
                        </tr>

                        {/* Expanded items */}
                        {expandedJobId === job.id && (
                          <tr>
                            <td colSpan={8} className="p-0">
                              <div className="w-full bg-white border-t border-neutral-200 px-6 py-4">
                                {loadingItems ? (
                                  <p className="text-sm text-neutral-500 py-4">
                                    Loading items...
                                  </p>
                                ) : jobItems.length === 0 ? (
                                  <p className="text-sm text-neutral-500 py-4">
                                    No items found.
                                  </p>
                                ) : (
                                  <div className="flex flex-col gap-4">
                                    {jobItems.some(
                                      (i) => i.status === "generated",
                                    ) && (
                                      <div className="flex justify-end">
                                        <Button
                                          variant="brand-primary"
                                          icon={<FeatherCheck />}
                                          onClick={() =>
                                            handleConfirmAll(job.id)
                                          }
                                        >
                                          Confirm All
                                        </Button>
                                      </div>
                                    )}
                                    <table className="w-full text-sm">
                                      <thead>
                                        <tr className="border-b border-neutral-200">
                                          <th className="px-3 py-2 text-left font-medium text-neutral-500">
                                            Name
                                          </th>
                                          <th className="px-3 py-2 text-left font-medium text-neutral-500">
                                            Description
                                          </th>
                                          <th className="px-3 py-2 text-left font-medium text-neutral-500">
                                            Final Context
                                          </th>
                                          <th className="px-3 py-2 text-center font-medium text-neutral-500">
                                            Status
                                          </th>
                                          <th className="px-3 py-2 text-center font-medium text-neutral-500">
                                            Actions
                                          </th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {jobItems.map((item) => (
                                          <tr
                                            key={item.id}
                                            className="border-b border-neutral-100"
                                          >
                                            <td className="px-3 py-2 font-medium max-w-[150px] truncate">
                                              {item.entity_name}
                                            </td>
                                            <td className="px-3 py-2 max-w-[200px] truncate text-neutral-600">
                                              {item.raw_input}
                                            </td>
                                            <td className="px-3 py-2">
                                              {editingItemId === item.id ? (
                                                <div className="flex flex-col gap-2">
                                                  <TextArea className="w-full">
                                                    <TextArea.Input
                                                      value={editText}
                                                      onChange={(e) =>
                                                        setEditText(
                                                          e.target.value.slice(
                                                            0,
                                                            750,
                                                          ),
                                                        )
                                                      }
                                                      placeholder="Edit context..."
                                                    />
                                                  </TextArea>
                                                  <span className="text-xs text-neutral-400">
                                                    {editText.length}/750
                                                  </span>
                                                  <div className="flex gap-2">
                                                    <Button
                                                      size="small"
                                                      variant="brand-primary"
                                                      icon={<FeatherCheck />}
                                                      onClick={() =>
                                                        handleSaveEdit(
                                                          job.id,
                                                          item.id,
                                                        )
                                                      }
                                                    >
                                                      Save
                                                    </Button>
                                                    <Button
                                                      size="small"
                                                      variant="neutral-secondary"
                                                      icon={<FeatherX />}
                                                      onClick={handleCancelEdit}
                                                    >
                                                      Cancel
                                                    </Button>
                                                  </div>
                                                </div>
                                              ) : (
                                                <span className="text-neutral-600 text-xs whitespace-pre-wrap break-words">
                                                  {item.final_context || (
                                                    <span className="text-neutral-400 italic">
                                                      Pending...
                                                    </span>
                                                  )}
                                                </span>
                                              )}
                                            </td>
                                            <td className="px-3 py-2 text-center">
                                              {itemStatusBadge(item.status)}
                                            </td>
                                            <td className="px-3 py-2 text-center">
                                              <div className="flex items-center justify-center gap-2">
                                                {item.status ===
                                                  "generated" && (
                                                  <>
                                                    <Button
                                                      size="small"
                                                      variant="neutral-secondary"
                                                      icon={<FeatherEdit2 />}
                                                      onClick={() =>
                                                        handleStartEdit(item)
                                                      }
                                                    >
                                                      Edit
                                                    </Button>
                                                    <Button
                                                      size="small"
                                                      variant="brand-primary"
                                                      icon={<FeatherCheck />}
                                                      onClick={() =>
                                                        handleConfirmItem(
                                                          job.id,
                                                          item.id,
                                                        )
                                                      }
                                                    >
                                                      Confirm
                                                    </Button>
                                                  </>
                                                )}
                                                {item.status === "failed" && (
                                                  <Button
                                                    size="small"
                                                    variant="neutral-secondary"
                                                    icon={<FeatherRefreshCw />}
                                                    onClick={() =>
                                                      handleRetryItem(
                                                        job.id,
                                                        item.id,
                                                      )
                                                    }
                                                  >
                                                    Retry
                                                  </Button>
                                                )}
                                              </div>
                                            </td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </DefaultPageLayout>
  );
};
